import { useEffect, useMemo, useRef, useState } from 'react'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import listPlugin from '@fullcalendar/list'
import interactionPlugin from '@fullcalendar/interaction'
import esLocale from '@fullcalendar/core/locales/es'
import { LoaderFunctionArgs, useLoaderData, useRevalidator, useSearchParams } from 'react-router-dom'
import { useCurrentHealthCenter, useIsAccountClosed } from '../../components/AppShell'
import { Alert, Button, ButtonAnchor, Modal, Panel, PanelBody, useToast } from '../../components/common'
import { CalendarEvent, CalendarFilters, fetchCalendarData, fetchProfessionalSchedules } from '../../api/calendar'
import { AgendaProcessor, MultipleAgendaProcessor, formatDateKey } from '../../lib/agenda'
import { trackEvent } from '../../lib/config'
import { useImpression } from '../../lib/useImpression'
import { useNavigateTo } from '../../lib/spaPath'
import { trackUpgradePromptClick } from '../../lib/upgradePrompt'
import styles from './BoxCalendarsPage.module.scss'
import { AppointmentModal } from './AppointmentModal'
import { SearchableSelect, SearchableOption } from '../../components/common'

type TooltipState = { event: CalendarEvent; x: number; y: number }
type CalendarView = 'timeGridDay' | 'dayGridMonth'
type DateRange = { readonly start: string; readonly end: string }

const NARROW_VIEWPORT_WIDTH = 768
const MONTH_CELL_MAX_EVENTS = 3
const DAY_NAMES_SHORT = ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa']

const MONTH_GRID_DAYS = 42
const MONDAY = 1

const isNarrowViewport = () => window.innerWidth < NARROW_VIEWPORT_WIDTH

const toRange = (start: Date, end: Date): DateRange => ({ start: start.toISOString(), end: end.toISOString() })

function getInitialRange(view: CalendarView, date: Date): DateRange {
  const [year, month, day] = [date.getFullYear(), date.getMonth(), date.getDate()]
  if (view === 'timeGridDay') {
    return toRange(new Date(year, month, day), new Date(year, month, day + 1))
  }
  const leadingDays = (new Date(year, month, 1).getDay() - MONDAY + 7) % 7
  return toRange(new Date(year, month, 1 - leadingDays), new Date(year, month, 1 - leadingDays + MONTH_GRID_DAYS))
}

function getCalendarFilters(searchParams: URLSearchParams, range: DateRange): CalendarFilters {
  const professional = searchParams.get('professional') ?? ''
  const patient = searchParams.get('patient') ?? ''
  return {
    box: searchParams.get('box') ?? '',
    professional: professional === '-1' ? '' : professional,
    patient: patient === '-1' ? '' : patient,
    ...range,
  }
}

export async function loader({ params, request }: LoaderFunctionArgs) {
  const healthCenterId = Number(params.healthCenterId)
  const view: CalendarView = isNarrowViewport() ? 'timeGridDay' : 'dayGridMonth'
  const date = new Date()
  const range = getInitialRange(view, date)
  const [calendar, schedules] = await Promise.all([
    fetchCalendarData(healthCenterId, getCalendarFilters(new URL(request.url).searchParams, range)),
    fetchProfessionalSchedules(healthCenterId),
  ])
  return { view, date, range, calendar, schedules }
}

function EventTooltip({ tooltip }: { tooltip: TooltipState }) {
  const event = tooltip.event
  const navigateTo = useNavigateTo()
  return (
    <div
      className={styles.meeting}
      style={{
        position: 'fixed',
        left: Math.max(8, tooltip.x - 320),
        top: tooltip.y,
        zIndex: 10000,
        background: 'white',
        boxShadow: '0 2px 12px rgba(0,0,0,0.25)',
        borderRadius: 6,
        padding: 12,
        width: 300,
      }}
    >
      <div className={styles.patientName} id="meeting_patient">
        {event.patient_name || '- no registrado -'}
      </div>
      <div className={styles.time} id="meeting_time">
        {event.time || '- no registrado -'}
      </div>
      <div className={styles.mettingList}>
        <div className={styles.mettingData}>
          <div className={styles.mettingIcon}>
            <i className="fa fa-user-md" />
          </div>
          <div className={styles.mettingInfo} id="meeting_professional">
            {event.professional || '- no registrado -'}
          </div>
        </div>
        <div className={styles.mettingData}>
          <div className={styles.mettingIcon}>
            <i className="fa fa-phone" />
          </div>
          <div className={styles.mettingInfo} id="meeting_phone">
            {event.patient_phone || '- no registrado -'}
          </div>
        </div>
        <div className={styles.mettingData}>
          <div className={styles.mettingIcon}>
            <i className="fa fa-envelope" />
          </div>
          <div className={styles.mettingInfo} id="meeting_email">
            {event.patient_email || '- no registrado -'}
          </div>
        </div>
      </div>
      <div className={styles.meetingInformation} id="meeting_description">
        {event.description}
      </div>
      <div className={styles.actions}>
        <Button
          variant="simple"
          className={styles.action}
          id="goToCR"
          onClick={() => {
            trackEvent('Clinical Record Opened', { source: 'calendar_tooltip' }, { transport: 'sendBeacon' })
            navigateTo(event.url_clinical_record)
          }}
        >
          Ver Ficha Clínica
        </Button>
      </div>
    </div>
  )
}

export default function BoxCalendarsPage() {
  const healthCenter = useCurrentHealthCenter()
  const closed = useIsAccountClosed()
  const showToast = useToast()
  const [searchParams, setSearchParams] = useSearchParams()
  const loaded = useLoaderData() as Awaited<ReturnType<typeof loader>>
  const { revalidate } = useRevalidator()
  const [range, setRange] = useState<DateRange>(loaded.range)
  const [rangeData, setRangeData] = useState(loaded.calendar)
  const [filterPatient, setFilterPatient] = useState(searchParams.get('patient') ?? '-1')
  const [filterProfessional, setFilterProfessional] = useState(searchParams.get('professional') ?? '-1')
  const [filterBox, setFilterBox] = useState(searchParams.get('box') ?? '')
  const [modalEvent, setModalEvent] = useState<CalendarEvent | null>(null)
  const [modalDate, setModalDate] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [tooltip, setTooltip] = useState<TooltipState | null>(null)
  const calendarRef = useRef<FullCalendar>(null)
  const lastRange = useRef<{ view: string; key: string } | null>(null)

  const professionalIds = useMemo(() => {
    const applied = searchParams.get('professional')
    if (applied && applied !== '-1') {
      return [applied]
    }
    return null
  }, [searchParams])

  const isLoaderRange = range.start === loaded.range.start && range.end === loaded.range.end
  const data = isLoaderRange ? loaded.calendar : rangeData

  useEffect(() => {
    if (!healthCenter || isLoaderRange) {
      return
    }
    let isCurrent = true
    void fetchCalendarData(healthCenter.id, getCalendarFilters(searchParams, range)).then((next) => {
      if (isCurrent) {
        setRangeData(next)
      }
    })
    return () => {
      isCurrent = false
    }
  }, [healthCenter, searchParams, range, isLoaderRange, loaded])

  const processor = useMemo(() => {
    const multipleProcessor = new MultipleAgendaProcessor()
    loaded.schedules.forEach((entry) => {
      multipleProcessor.add(
        new AgendaProcessor(entry.schedule ?? {}, entry.professional.event_calendars, loaded.calendar.session_duration),
        { id: entry.professional.id, name: entry.professional.name },
      )
    })
    return multipleProcessor
  }, [loaded])
  const [emptyScheduleOpen, setEmptyScheduleOpen] = useState(() => processor.isEmpty())

  const lastNarrow = useRef(loaded.view === 'timeGridDay')
  useEffect(() => {
    const applyViewForViewport = () => {
      const narrow = isNarrowViewport()
      if (narrow === lastNarrow.current) {
        return
      }
      lastNarrow.current = narrow
      calendarRef.current?.getApi().changeView(narrow ? 'timeGridDay' : 'dayGridMonth')
    }
    window.addEventListener('resize', applyViewForViewport)
    return () => window.removeEventListener('resize', applyViewForViewport)
  }, [])

  const trialExpired = !data.trial_features_enabled
  useImpression('Upgrade Prompt Shown', trialExpired ? { prompt: 'trial_expired', surface: 'calendar' } : null)
  useImpression('Onboarding Hint Shown', data.has_professional_without_schedule ? { hint: 'professionals_without_schedule' } : null)
  useImpression('Onboarding Hint Shown', emptyScheduleOpen ? { hint: 'no_schedule_modal' } : null)

  if (!healthCenter) {
    return null
  }

  const closeEmptyScheduleModal = () => {
    trackEvent('Onboarding Hint Dismissed', { hint: 'no_schedule_modal' })
    setEmptyScheduleOpen(false)
  }

  const disabledWeekDays = processor.disabledWeekDays(professionalIds)
  const disabledDates = processor.disabledDates(professionalIds)

  const getUnavailableReason = (date: Date): 'past_date' | 'no_availability' | null => {
    const dateIso = formatDateKey(date)
    if (dateIso < formatDateKey(new Date())) {
      return 'past_date'
    }
    if (disabledWeekDays.includes(date.getDay()) || disabledDates.includes(dateIso)) {
      return 'no_availability'
    }
    if (dateIso === formatDateKey(new Date()) && !processor.hasTimesLeftToday(professionalIds)) {
      return 'no_availability'
    }
    return null
  }

  const openNewAppointment = (dateIso: string | null, source: 'date_click' | 'new_button') => {
    // The three warnings below are the paywall and the friction the booking funnel
    // loses people to, so each one is an event rather than only a toast.
    if (!data.trial_features_enabled) {
      trackEvent('Appointment Blocked', { reason: 'trial_expired' })
      showToast('warning', 'El periodo de prueba de la Agenda ha terminado. Para seguir utilizándolo debes subir tu cuenta a premium')
      return
    }
    if (dateIso) {
      const reason = getUnavailableReason(new Date(`${dateIso}T00:00:00`))
      if (reason === 'past_date') {
        trackEvent('Appointment Blocked', { reason })
        showToast('warning', 'No es posible agendar una hora en una fecha pasada')
        return
      }
      if (reason === 'no_availability') {
        trackEvent('Appointment Blocked', { reason })
        showToast('warning', 'No hay horarios disponibles para ese día')
        return
      }
    }
    trackEvent('Appointment Modal Opened', { source })
    setModalEvent(null)
    setModalDate(dateIso)
    setModalOpen(true)
  }

  const patientFilterOptions: SearchableOption[] = useMemo(
    () => [
      { value: '-1', label: '-- Todos los pacientes --' },
      ...data.patients.map((patient) => ({ value: String(patient.id), label: patient.name })),
    ],
    [data.patients],
  )

  const professionalFilterOptions: SearchableOption[] = useMemo(
    () => [
      { value: '-1', label: '-- Todos los profesionales --' },
      ...data.professionals.map((professional) => ({ value: String(professional.id), label: professional.name })),
    ],
    [data.professionals],
  )

  const boxFilterOptions: SearchableOption[] = useMemo(
    () => data.boxes.map((box) => ({ value: String(box.id), label: box.name })),
    [data.boxes],
  )

  const applyFilters = () => {
    const hasProfessional = filterProfessional !== '-1' && filterProfessional !== ''
    trackEvent('Calendar Filtered', {
      has_patient: filterPatient !== '-1' && filterPatient !== '',
      has_professional: hasProfessional,
      has_box: filterBox !== '',
      professional_id: hasProfessional ? Number(filterProfessional) : undefined,
      box_id: filterBox !== '' ? Number(filterBox) : undefined,
    })
    const next = new URLSearchParams()
    next.set('patient', filterPatient)
    next.set('professional', filterProfessional)
    if (filterBox) {
      next.set('box', filterBox)
    }
    setSearchParams(next)
  }

  const fcEvents = data.events.map((event) => ({
    id: event.id,
    title: event.title,
    start: event.start,
    end: event.end,
    color: event.color,
    editable: false,
    extendedProps: { hcEvent: event },
  }))

  const actionButtons = (
    <Panel className={styles.actionButtons}>
      <PanelBody>
        {!closed && (
          <div className="text-center">
            <ButtonAnchor
              href="#"
              variant="success"
              className={styles.actionButton}
              onClick={(clickEvent) => {
                clickEvent.preventDefault()
                openNewAppointment(null, 'new_button')
              }}
            >
              Agendar Hora
            </ButtonAnchor>
          </div>
        )}
        <div className="text-center">
          <ButtonAnchor
            href={`/health_centers/${healthCenter.id}/professionals`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.actionButton}
            onClick={() => trackEvent('Settings Opened', { section: 'accounts', source: 'calendar_actions' })}
          >
            Horario de profesionales
          </ButtonAnchor>
        </div>
        <div className="text-center">
          <ButtonAnchor
            href={`/professionals/health_centers/${healthCenter.id}/edit#calendar`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.actionButton}
            onClick={() => trackEvent('Settings Opened', { section: 'schedule', source: 'calendar_actions' })}
          >
            Configuración de agenda
          </ButtonAnchor>
        </div>
      </PanelBody>
    </Panel>
  )

  return (
    <>
      {trialExpired && (
        <Alert kind="warning" centered data-testid="payment-alert">
          El periodo de prueba de la Agenda ha terminado.
          <br />
          Para seguir utilizándolo, puedes actualizar tu cuenta a Premium{' '}
          <a
            href={`/professionals/health_centers/${healthCenter.id}/edit`}
            style={{ textDecoration: 'underline' }}
            onClick={trackUpgradePromptClick('trial_expired', 'calendar')}
          >
            aquí
          </a>
        </Alert>
      )}
      <div className={`row ${styles.medicalCalendar}`}>
        <div className="col-lg-1 hidden-xs hidden-sm hidden-md" />
        <div className="col-md-3 col-lg-3">
          <Panel className={styles.actionButtons}>
            <PanelBody>
              <h3 className={`text-center blue-subtitle ${styles.filterHeading}`}>Agenda de Pacientes</h3>
              <form
                className={styles.calendarFilterContainer}
                onSubmit={(submitEvent) => {
                  submitEvent.preventDefault()
                  applyFilters()
                }}
              >
                <div className={`col-xs-12 ${styles.calendarFilter}`}>
                  <div className={styles.calendarFilterLabel}>
                    <i className="fa fa-user" />
                  </div>
                  <div className={styles.calendarFilterInput}>
                    <SearchableSelect
                      id="patient"
                      placeholder="-- Todos los pacientes --"
                      value={filterPatient}
                      options={patientFilterOptions}
                      onChange={setFilterPatient}
                    />
                  </div>
                </div>
                <div className={`col-xs-12 ${styles.calendarFilter}`}>
                  <div className={styles.calendarFilterLabel}>
                    <i className="fa fa-user-md" />
                  </div>
                  <div className={styles.calendarFilterInput}>
                    <SearchableSelect
                      id="professional"
                      placeholder="-- Todos los profesionales --"
                      value={filterProfessional}
                      options={professionalFilterOptions}
                      onChange={setFilterProfessional}
                    />
                  </div>
                </div>
                <div className={`col-xs-12 ${styles.calendarFilter}`}>
                  <div className={styles.calendarFilterLabel}>
                    <i className="fa fa-map-marker" />
                  </div>
                  <div className={styles.calendarFilterInput}>
                    <SearchableSelect
                      id="box"
                      placeholder="Box"
                      value={filterBox || String(data.selected_box_id ?? '')}
                      options={boxFilterOptions}
                      onChange={setFilterBox}
                    />
                  </div>
                </div>
                <div className={`col-xs-12 text-center ${styles.calendarFilterSubmit}`}>
                  <Button type="submit">
                    <i className="fa fa-search" /> Buscar
                  </Button>
                </div>
              </form>
            </PanelBody>
          </Panel>
          <div className="hidden-xs hidden-sm">{actionButtons}</div>
        </div>
        <div className="col-xs-12 col-md-9 col-lg-7">
          <div className={`${styles.alertContainer} text-center`}>
            {data.has_professional_without_schedule && (
              <div className={styles.scheduleWarning}>
                <p>
                  <i className="material-icons">warning</i>{' '}
                  Hay profesionales que no tienen asignado un horario de trabajo, por lo que no podrás agendarles consultas. Para asignar el
                  horario de trabajo de un profesional{' '}
                  <a
                    href={`/health_centers/${healthCenter.id}/professionals`}
                    onClick={() =>
                      trackEvent(
                        'Onboarding Hint Clicked',
                        { hint: 'professionals_without_schedule', target: 'schedule_settings' },
                        { transport: 'sendBeacon' },
                      )
                    }
                  >
                    haz click aquí.
                  </a>
                </p>
              </div>
            )}
          </div>
          <Panel>
            <PanelBody className={styles.noPaddingSm}>
              <div className={`col-xs-12 ${styles.noPaddingSm}`}>
                <div id="hc-full-calendar" style={{ padding: 0 }}>
                  <FullCalendar
                    ref={calendarRef}
                    plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
                    initialView={loaded.view}
                    initialDate={loaded.date}
                    locale={esLocale}
                    firstDay={MONDAY}
                    dayHeaderContent={(arg) =>
                      arg.view.type === 'timeGridDay' || arg.view.type === 'listDay'
                        ? undefined
                        : arg.view.type === 'dayGridMonth'
                          ? DAY_NAMES_SHORT[arg.dow]
                          : `${DAY_NAMES_SHORT[arg.dow]} ${arg.date.getDate()}/${arg.date.getMonth() + 1}`
                    }
                    timeZone="local"
                    headerToolbar={{
                      left: 'prev,next today',
                      center: 'title',
                      right: isNarrowViewport() ? 'timeGridDay,listDay' : 'dayGridMonth,timeGridWeek,timeGridDay,listDay',
                    }}
                    eventTimeFormat={{ hour: 'numeric', minute: '2-digit', hour12: false }}
                    allDaySlot={false}
                    slotDuration="00:15:00"
                    snapDuration="00:15:00"
                    slotMinTime="07:00:00"
                    slotMaxTime="22:00:00"
                    scrollTime="08:00:00"
                    nowIndicator
                    dayMaxEvents={MONTH_CELL_MAX_EVENTS}
                    selectable={!closed}
                    events={fcEvents}
                    selectAllow={(span) => getUnavailableReason(span.start) === null}
                    dayCellClassNames={(arg) => (getUnavailableReason(arg.date) ? ['hc-disabled-day'] : [])}
                    datesSet={(arg) => {
                      // FullCalendar can report a range it already reported; only a change of
                      // view or of range is a navigation.
                      const previous = lastRange.current
                      const current = { view: arg.view.type, key: `${arg.view.type}:${arg.startStr}:${arg.endStr}` }
                      lastRange.current = current
                      if (previous !== null && previous.key === current.key) {
                        return
                      }
                      setRange(toRange(arg.start, arg.end))
                      if (previous === null) {
                        return
                      }
                      trackEvent('Calendar Navigated', {
                        view: current.view,
                        trigger: previous.view === current.view ? 'range_change' : 'view_change',
                        range_start: arg.startStr,
                        range_end: arg.endStr,
                      })
                    }}
                    dateClick={closed ? undefined : (arg) => openNewAppointment(formatDateKey(arg.date), 'date_click')}
                    select={closed ? undefined : (arg) => openNewAppointment(formatDateKey(arg.start), 'date_click')}
                    eventClick={(arg) => {
                      const hcEvent = arg.event.extendedProps.hcEvent as CalendarEvent
                      trackEvent('Appointment Modal Opened', { source: 'event_click' })
                      setTooltip(null)
                      setModalEvent(hcEvent)
                      setModalDate(null)
                      setModalOpen(true)
                    }}
                    eventMouseEnter={(arg) => {
                      const hcEvent = arg.event.extendedProps.hcEvent as CalendarEvent
                      const rect = arg.el.getBoundingClientRect()
                      setTooltip({ event: hcEvent, x: rect.left, y: rect.top })
                    }}
                    eventMouseLeave={() => setTooltip(null)}
                  />
                </div>
              </div>
            </PanelBody>
          </Panel>
        </div>
        <div className="col-lg-1 hidden-xs hidden-sm hidden-md" />
        <div className="col-xs-12 hidden-md hidden-lg">{actionButtons}</div>
      </div>

      {tooltip && <EventTooltip tooltip={tooltip} />}

      <Modal
        open={emptyScheduleOpen}
        onClose={closeEmptyScheduleModal}
        id="empty-schedule-modal"
        header={<h4 className={`modal-title blue-subtitle ${styles.centered}`}>No hay horarios asignados</h4>}
      >
        <div className="row">
          <div className="col-xs-12">
            <p className={styles.emptyScheduleText}>
              Para comenzar, debes asignarle un horario de trabajo a los profesionales de tu centro. Puedes hacerlo{' '}
              <a
                href={`/health_centers/${healthCenter.id}/professionals`}
                onClick={() =>
                  trackEvent('Onboarding Hint Clicked', { hint: 'no_schedule_modal', target: 'schedule_settings' }, { transport: 'sendBeacon' })
                }
              >
                haciendo click aquí.
              </a>
            </p>
          </div>
        </div>
      </Modal>

      {modalOpen && (
        <AppointmentModal
          healthCenterId={healthCenter.id}
          data={data}
          processor={processor}
          event={modalEvent}
          initialDate={modalDate}
          onClose={() => setModalOpen(false)}
          onSaved={(result) => {
            setModalOpen(false)
            showToast('success', result.message ?? 'Tu hora ha sido agendada')
            revalidate()
          }}
          onDeleted={(_id, message) => {
            setModalOpen(false)
            showToast('success', message)
            revalidate()
          }}
        />
      )}
    </>
  )
}
